import { TechMetadataMediaInfoField, TechMetadataSection } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { isNullOrUndefinedOrEmpty } from '@ibm-aspera/connect-sdk-js/dist/esm/utils';
import { Grid, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { EllipsisTooltip, PolarisIcon } from '@tmt-insights/oms_fe_core/columns';

type MediaInfoProps = {
  fileType: string;
  mediaInfo?: TechMetadataSection<TechMetadataMediaInfoField>;
  mismatchedFields: string[];
};

const MediaInfo = ({ mediaInfo, mismatchedFields }: MediaInfoProps) => {
  const filterNonCompareFields = (fields: TechMetadataMediaInfoField[]) => {
    return fields.filter((field) => !field.compare_view_only);
  };

  const noInfoAvailable = (
    <TitleWrapper>
      <Typography variant="h6" sx={{ fontStyle: 'italic', opacity: 0.5, mt: 2 }}>
        No media info available.
      </Typography>
    </TitleWrapper>
  );

  if (!mediaInfo) {
    return noInfoAvailable;
  }

  return (
    <>
      {mediaInfo.name ? (
        <Typography variant="subtitle2" sx={{ opacity: 0.7, mb: 1 }}>
          {mediaInfo.name}
        </Typography>
      ) : null}
      <Grid container spacing={1}>
        {filterNonCompareFields(mediaInfo.fields).map((field) => (
          <InfoTile key={field.field} label={field.label} highlight={mismatchedFields.includes(field.field)}>
            {isNullOrUndefinedOrEmpty(field.value) ? '-' : String(field.value)}
          </InfoTile>
        ))}
      </Grid>
    </>
  );
};

export default MediaInfo;

const InfoTile = ({ label, children, highlight = false }: { label: string; children: React.ReactNode; highlight?: boolean }) => {
  const tileRef = useRef<any>(null);
  const [maxWidth, setMaxWidth] = useState(200);

  useEffect(() => {
    if (tileRef.current) {
      setMaxWidth(tileRef.current.clientWidth - tileRef.current.children[0].clientWidth - 32);
    }
  }, [tileRef.current]);

  return (
    <Grid item xs={6}>
      <TileWrapper ref={tileRef} container highlight={highlight}>
        <LabelWrapper>
          {highlight && <PolarisIcon icon="error_circle" width={20} height={20} />}
          <span>{label}</span>
        </LabelWrapper>
        {!tileRef.current ? (
          <PolarisIcon icon="loading_dots" />
        ) : typeof children === 'string' && children?.length > 0 ? (
          <EllipsisTooltip text={children} maxWidth={maxWidth} />
        ) : (
          children || '-'
        )}
      </TileWrapper>
    </Grid>
  );
};

const TitleWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const TileWrapper = styled(Grid, { shouldForwardProp: (prop) => prop !== 'highlight' })<{ highlight?: boolean }>`
  background: ${({ theme }) => theme.palette.background.default};
  min-height: 44px;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  padding: ${({ theme }) => theme.spacing(1, 2)};
  height: 100%;
  align-items: center;

  > div:first-of-type {
    font-size: 1.1rem;
    padding-right: ${({ theme }) => theme.spacing(2)};
  }

  > span {
    font-size: 1.1rem;
    opacity: 0.8;
  }
`;

const LabelWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};

  > span {
    opacity: 0.5;
  }

  svg {
    color: ${({ theme }) => theme.palette.warning.main};
    opacity: 1;
    flex-shrink: 0;
  }
`;
